import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

/**
 * Banner que oferece instalacao do app (PWA add-to-home-screen).
 *
 * Captura o evento `beforeinstallprompt` (Chrome/Edge/Samsung) e mostra
 * um banner que o usuário pode aceitar para chamar `prompt()`. Em iOS
 * Safari não ha esse evento, entao mostra instrucao manual.
 *
 * Persistencia: se usuário dispensar, salva em localStorage para não
 * mostrar novamente nas próximas 30 dias.
 *
 * Sprint ZZZ.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const LS_KEY = 'octawash:install-banner-dismissed-at';
const COOLDOWN_DAYS = 30;

function isStandalone(): boolean {
  // PWA já instalado
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function dismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(LS_KEY);
    if (!ts) return false;
    const diasAgo = (Date.now() - Number(ts)) / 86_400_000;
    return diasAgo < COOLDOWN_DAYS;
  } catch {
    return false;
  }
}

export function InstallAppBanner() {
  const [promptEvt, setPromptEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) {
      setDismissed(true);
      return;
    }

    if (isIOS()) {
      // iOS Safari não tem beforeinstallprompt — mostra dica manual
      setShowIosTip(true);
      return;
    }

    function handler(e: Event) {
      e.preventDefault();
      setPromptEvt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dispensar() {
    try {
      localStorage.setItem(LS_KEY, String(Date.now()));
    } catch {
      // ignore (e.g., private browsing)
    }
    setDismissed(true);
  }

  async function instalar() {
    if (!promptEvt) return;
    await promptEvt.prompt();
    const choice = await promptEvt.userChoice;
    if (choice.outcome === 'accepted') {
      setPromptEvt(null);
      setDismissed(true);
    } else {
      dispensar();
    }
  }

  if (dismissed) return null;
  if (!promptEvt && !showIosTip) return null;

  return (
    <div
      className="bg-primary fixed bottom-0 left-0 right-0 z-50 p-3 text-white shadow-lg md:bottom-4 md:left-1/2 md:right-auto md:max-w-md md:-translate-x-1/2 md:rounded-lg"
      role="region"
      aria-label="Instalar Octa Pontos como app"
    >
      <div className="flex items-start gap-3">
        <div className="bg-primary-foreground/10 mt-0.5 rounded-md p-2">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Instalar Octa Pontos como app</p>
          {promptEvt ? (
            <p className="text-xs opacity-90">
              Adicione na tela inicial para acesso rapido sem abrir o navegador.
            </p>
          ) : (
            <p className="text-xs opacity-90">
              Toque no botao Compartilhar do Safari e escolha &quot;Adicionar &agrave; Tela de
              In&iacute;cio&quot;.
            </p>
          )}
          <div className="mt-2 flex gap-2">
            {promptEvt && (
              <Button size="sm" variant="secondary" onClick={instalar} className="h-8 text-xs">
                Instalar agora
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={dispensar}
              className="text-primary-foreground h-8 text-xs hover:bg-white/10"
            >
              Agora não
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dispensar}
          aria-label="Fechar"
          className="text-primary-foreground/80 hover:text-primary-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
